import { ICommonListModel, IUsersFilterResponse } from "api";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  HrEmployeeArchiveDocument,
  HrEmployeeGetByIdDocument,
  HrEmployeeListDocument,
  HrEmployeePaginatedDocument,
  HrEmployeeCreateDocument,
  HrEmployeeUnarchiveDocument,
  HrEmployeeUpdateDocument,
  HrEmployeeDeleteDocument,
  UsersForEmployeeCreationDocument,
  HrOfficeListDocument,
  HrEmployeeUserDetailsDocument,
  HrEmployeeListCompensationHistoryDocument,
} from "api/generated";

import { Optional, ScopeEnum, ResourceEnum } from "config";

import {
  IHrEmployeeModel,
  IHrEmployeeCreateVars,
  IHrEmployeePaginatedResponse,
  IHrEmployeePaginatedVars,
  IHrEmployeeUpdateVars,
  IHrEmployeeListModel,
  HrEmployeeUserDetails,
  IHrEmployeeListCompensationHistoryModel,
} from "./types";

export async function getHrOfficeList(): Promise<ICommonListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrOfficeListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrOfficeList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getUsersForEmployeeCreate(): Promise<
  IUsersFilterResponse[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UsersForEmployeeCreationDocument,
      fetchPolicy: "no-cache",
    });

    return res?.data?.usersForEmployeeCreation ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeListCompensationHistory(
  employeeId: number
): Promise<IHrEmployeeListCompensationHistoryModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeListCompensationHistoryDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeListCompensationHistory ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeUserDetails(
  userId: number
): Promise<HrEmployeeUserDetails> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeUserDetailsDocument,
      variables: {
        userId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeUserDetails;
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployee(
  id: number
): Promise<Optional<IHrEmployeeModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeGetByIdDocument,
      variables: {
        id: id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeGetById;
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeePaginated(
  variables: IHrEmployeePaginatedVars
): Promise<Optional<IHrEmployeePaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeePaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeList(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<IHrEmployeeListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeListDocument,
      variables: {
        scope,
        resource,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeList || [];
  } catch (error) {
    throw error;
  }
}

export async function createHrEmployee(
  variables: IHrEmployeeCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all employee lists so they refetch with the new item
    executeEviction(client, {
      typename: "HrEmployee",
      fieldNames: ["hrEmployeeListPaginated", "hrEmployeeList"],
    });

    return Boolean(res?.data?.hrEmployeeCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateHrEmployee(
  variables: IHrEmployeeUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "HrEmployee",
      id: variables.id?.toString(),
      fieldNames: [
        "hrEmployeeListPaginated",
        "hrEmployeeList",
        "hrEmployeeGetById",
        "hrEmployeeListCompensationHistory",
      ],
    });

    return Boolean(res?.data?.hrEmployeeUpdate);
  } catch (error) {
    throw error;
  }
}

export async function archiveHrEmployee(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeArchiveDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the archived entity and all lists containing it
    executeEviction(client, {
      typename: "HrEmployee",
      id: id.toString(),
      fieldNames: [
        "hrEmployeeListPaginated",
        "hrEmployeeList",
        "hrEmployeeGetById",
      ],
    });

    return Boolean(res?.data?.hrEmployeeArchive);
  } catch (error) {
    throw error;
  }
}

export async function deleteHrEmployee(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "HrEmployee",
      id: id.toString(),
      fieldNames: [
        "hrEmployeeListPaginated",
        "hrEmployeeList",
        "hrEmployeeGetById",
      ],
    });

    return Boolean(res?.data?.hrEmployeeDelete);
  } catch (error) {
    throw error;
  }
}

export async function unarchiveHrEmployee(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeUnarchiveDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the unarchived entity and all lists containing it
    executeEviction(client, {
      typename: "HrEmployee",
      id: id.toString(),
      fieldNames: [
        "hrEmployeeListPaginated",
        "hrEmployeeList",
        "hrEmployeeGetById",
      ],
    });

    return Boolean(res?.data?.hrEmployeeUnarchive);
  } catch (error) {
    throw error;
  }
}
